import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { VALID_DIMENSION_FILTERS, MATCHING_SETS } from '../sbSharedRefineMenuConstants';

// components
import { SbSharedRefineMenuFacet } from '../SbSharedRefineMenuFacet/SbSharedRefineMenuFacet';
import {
    TradeOnboardingCoachmarkConditionalLoader,
    SB_PLACEMENT,
} from '../../../global/tradeOnboarding/TradeOnboardingCoachmarkConditionalLoader';
import { SbRespTradeFreeShippingCoachmarkLazy } from '../../SbRespTradeFreeShippingCoachmark/SbRespTradeFreeShippingCoachmarkLazy';

/* eslint-disable react/prop-types, react/display-name */

export class SbSharedRefineMenuFiltersComponent extends React.Component {
    render() {
        const {
            intl,
            user,
            viewer,
            filters,
            currency,
            isClient,
            filterMap,
            itemSearch,
            initiallyExpanded,
            isTrade,
        } = this.props;
        let dimensionsDisplayed = false;

        return (
            <React.Fragment>
                {filters.map(filter => {
                    const {
                        values,
                        totalCount,
                        name: filterName,
                        localizedFilterName: title,
                    } = filter;
                    const {
                        component: FilterComponent,
                        title: FilterTitle,
                        showTradeOnboardingCoachmark,
                        showFreeShippingCoachmark,
                    } = filterMap[filterName] || {};
                    const titleOverride = FilterTitle && intl.formatMessage(FilterTitle);
                    /**
                     * height, width, length and depth are returned as separate filters but need to be
                     * displayed in one "Dimensions" facet.  When the first valid dimension type filter is hit
                     * we will setup the dimensions facet.  Once the dimensions facet is setup we will
                     * ignore the rest of the dimension filters.
                     *
                     * The dimensions filter component will account and setup all available dimension filters in one facet
                     */

                    if (VALID_DIMENSION_FILTERS.includes(filterName)) {
                        if (dimensionsDisplayed) {
                            return null;
                        }
                        dimensionsDisplayed = true;
                    }

                    if (filterName === MATCHING_SETS && !viewer.isMatchedSetsEnabled) {
                        return null;
                    }

                    let coachmark = null;
                    if (showTradeOnboardingCoachmark) {
                        coachmark = (
                            <TradeOnboardingCoachmarkConditionalLoader
                                type={SB_PLACEMENT}
                                item={null}
                                user={user}
                            />
                        );
                    } else if (isClient && isTrade && showFreeShippingCoachmark) {
                        coachmark = (
                            <React.Suspense fallback={null}>
                                <SbRespTradeFreeShippingCoachmarkLazy />
                            </React.Suspense>
                        );
                    }

                    return (
                        FilterComponent && (
                            <SbSharedRefineMenuFacet
                                key={filterName}
                                storageFacetKey={filterName}
                                title={titleOverride || title}
                                initiallyExpanded={initiallyExpanded(filterName)}
                                coachmark={coachmark}
                            >
                                <FilterComponent
                                    user={user}
                                    viewer={viewer}
                                    values={values}
                                    filter={filter}
                                    filters={filters}
                                    currency={currency}
                                    isClient={isClient}
                                    totalCount={totalCount}
                                    filterName={filterName}
                                    itemSearch={itemSearch}
                                />
                            </SbSharedRefineMenuFacet>
                        )
                    );
                })}
            </React.Fragment>
        );
    }
}

SbSharedRefineMenuFiltersComponent.defaultProps = {
    initiallyExpanded: () => true,
};

SbSharedRefineMenuFiltersComponent.propTypes = {
    user: PropTypes.object,
    isClient: PropTypes.bool,
    filters: PropTypes.array.isRequired,
    viewer: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    filterMap: PropTypes.object.isRequired,
    itemSearch: PropTypes.object.isRequired,
    initiallyExpanded: PropTypes.func.isRequired,
    uriRef: PropTypes.string,
    isTrade: PropTypes.bool,
};

SbSharedRefineMenuFiltersComponent = injectIntl(SbSharedRefineMenuFiltersComponent);

export const SbSharedRefineMenuFilters = createFragmentContainer(
    SbSharedRefineMenuFiltersComponent,
    {
        viewer: graphql`
            fragment SbSharedRefineMenuFilters_viewer on Viewer {
                isMatchedSetsEnabled: isEnabled(key: "enableMatchedSetsFacet")
                ...SbSharedRefineMenuFilterMetal_viewer
                ...SbSharedRefineMenuFilterStone_viewer
                ...SbSharedRefineMenuFilterOrigin_viewer
                ...SbSharedRefineMenuFilterCreator_viewer
                ...SbSharedRefineMenuFilterMaterial_viewer
                ...SbRespRefineMenuFilterDeliveryOptions_viewer
                ...SbMobileRefineMenuFilterDeliveryOptions_viewer
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefineMenuFilters_itemSearch on ItemSearchQueryConnection {
                ...SbRespRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStone_itemSearch
                ...SbSharedRefineMenuFilterMetal_itemSearch
                ...SbSharedRefineMenuFilterColor_itemSearch
                ...SbMobileRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStyle_itemSearch
                ...SbSharedRefineMenuFilterOrigin_itemSearch
                ...SbSharedRefineMenuFilterPeriod_itemSearch
                ...SbRespRefineMenuFilterCategory_itemSearch
                ...SbRespRefineMenuFilterStoneCut_itemSearch
                ...SbSharedRefineMenuFilterCreator_itemSearch
                ...SbMobileRefineMenuFilterStoneCut_itemSearch
                ...SbMobileRefineMenuFilterCategory_itemSearch
                ...SbSharedRefineMenuFilterMaterial_itemSearch
                ...SbRespRefineMenuFilterDeliveryOptions_itemSearch
                ...SbMobileRefineMenuFilterDeliveryOptions_itemSearch
                ...SbSharedRefineMenuFilterItemType_itemSearch
                ...SbRespRefineMenuFilterDimensions_itemSearch
                ...SbRespRefineMenuFilterCaratWeight_itemSearch
                ...SbMobileRefineMenuFilterDimensions_itemSearch
                ...SbMobileRefineMenuFilterCaratWeight_itemSearch
                ...SbSharedRefineMenuFilterAvailability_itemSearch
                ...SbSharedRefineMenuFilterMatchingSets_itemSearch
            }
        `,
        filters: graphql`
            fragment SbSharedRefineMenuFilters_filters on SearchBrowseFilter @relay(plural: true) {
                name
                totalCount
                localizedFilterName
                ...SbRespRefineMenuFilterCategory_filters
                ...SbMobileRefineMenuFilterCategory_filters
                ...SbRespRefineMenuFilterDimensions_filters
                ...SbMobileRefineMenuFilterDimensions_filters
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    stats {
                        key
                        values
                    }
                }
            }
        `,
        user: graphql`
            fragment SbSharedRefineMenuFilters_user on User {
                ...SbRespRefineMenuFilterPrice_user
                ...SbRespRefineMenuFilterDimensions_user
                ...SbMobileRefineMenuFilterPrice_user
                ...SbMobileRefineMenuFilterDimensions_user
                ...TradeOnboardingCoachmarkConditionalLoader_user
            }
        `,
    }
);
